import * as React from 'react';
import {Typography, Box, TextField, Grid, Autocomplete} from '@mui/material';
import {useState, useContext, useEffect} from 'react';
import {consistentSpacing} from './variables';
import APIServiceFormContext from '../../context/APIServiceFormContext';

/**
 * A component representing an AutoComplete select input.
 *
 * @component
 * @param {Object} props - The component properties.
 * @param {string} props.title - The title for the AutoComplete.
 * @param {Object} props.titleSX - The style object for the title.
 * @param {string} props.subtitle - The subtitle for the AutoComplete.
 * @param {Array} props.options - The options for the AutoComplete.
 * @param {number} props.childXS - The size for extra small screens.
 * @param {number} props.childSM - The size for small screens.
 * @param {number} props.childMD - The size for medium screens.
 * @param {number} props.childLG - The size for large screens.
 * @param {string} props.width - The width of the AutoComplete.
 * @returns {JSX.Element} The AutoCompleteSelect component.
 */
export const AutoCompleteSelect = ({title, titleSX, subtitle, options, childXS, childSM, childMD, childLG, width}) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const {setValidationFunc, setSendDataArgs, savedValue} = useContext(APIServiceFormContext);

  const xs = childXS === undefined ? 12 : childXS;
  const sm = childSM === undefined ? 6 : childSM;
  const md = childMD === undefined ? sm : childMD;
  const lg = childLG === undefined ? md : childLG;

  /**
   * Handles the change of value in the AutoComplete.
   *
   * @param {Event} event - The change event.
   * @param {Object} newValue - The newly selected value.
   */
  const handleValueChange = (event, newValue) => {
    setSelectedValue(newValue);
  };

  /**
   * Wrapper function for the component validation function.
   *
   * @returns {Function} The component validation function.
   */
  const componentValidationWrapper = () => {
    function componentValidation() {
      if (selectedValue === null) return 'Please select an option.';
      return '';
    }

    return componentValidation;
  };

  useEffect(() => {
    setSelectedValue(savedValue === undefined ? null : savedValue);
  }, [savedValue]);
  useEffect(() => {
    setValidationFunc(componentValidationWrapper);
    setSendDataArgs({data: selectedValue});
  }, [selectedValue]);
  useEffect(() => setSelectedValue(savedValue === undefined ? null : savedValue), [title]);

  return (
    <Grid container rowGap={consistentSpacing} sx={{width: '100%', maxWidth: '750px'}}>
      <Grid item xs={12}>
        <Typography component={'p'} variant="subtitle0" my={{xs: 0, sm: 3}} fontWeight={450} sx={titleSX}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12} display={subtitle === '' || subtitle === undefined ? 'none' : 'block'}>
        <Typography component={'p'} variant="subtitle0" my={{xs: 0, sm: 3}}>
          {subtitle}
        </Typography>
      </Grid>
      <Grid
        item
        container
        gap={consistentSpacing}
        sx={{
          width: '100%',
          maxWidth: '750px',
          boxSizing: 'border-box',
          justifyContent: 'space-between',
        }}
      >
        <Grid
          item
          xs={xs}
          sm={sm}
          md={md}
          lg={lg}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Autocomplete
            id="country-select"
            sx={{
              width:
                width === undefined
                  ? {
                      xs: '90%',
                      sm: '50%',
                    }
                  : width,
            }}
            options={options}
            blurOnSelect
            autoHighlight
            getOptionLabel={option => option.label}
            renderOption={(props, option) => (
              <Box component="li" sx={{'& > img': {mr: 2, flexShrink: 0}}} {...props}>
                <img
                  loading="lazy"
                  width="20"
                  srcSet={`https://flagcdn.com/w40/${option.value.toLowerCase()}.png 2x`}
                  src={`https://flagcdn.com/w20/${option.value.toLowerCase()}.png`}
                  alt=""
                />
                {option.label} ({option.value})
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Choose a country"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off', // disable autocomplete and autofill
                }}
              />
            )}
            onChange={handleValueChange}
            value={selectedValue}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
